import classNames from "classnames";
import { FunctionComponent, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import ButtonSmily from "@Components/atoms/ButtonSmily";
import EstimatedDelivery from "@Components/atoms/EstimatedDelivery";
import { ReactComponent as IconArrowRight } from "@Icons/16x16/arrow-right.svg";
import { ProductInfosData, Section, Tile } from "@Types/pagify";
import { getLink } from "@Utils/pagify";
import "./style.scss";

type Items = "product" | "price" | "delivery";

export const ProductDetails: FunctionComponent<{
  productInfosData: ProductInfosData;
  section: Section;
  tiles: Tile[];
}> = ({ productInfosData, section, tiles }) => {
  const intl = useIntl();
  const localeSlug = intl.locale;

  const [selectedItem, setSelectedItem] = useState<Items>("product");

  const navigationItems: Items[] = ["product", "price", "delivery"];

  const tile = tiles[0];
  const productTitle = productInfosData.title;
  const productDescription = productInfosData.description;

  const priceInfos = tile.metadata?.web?.products;

  const deliveryDatesRange = tile.metadata?.deliveryDaysRange;
  const date1 = new Date();
  const date2 = new Date();
  if (deliveryDatesRange) {
    date1.setDate(date1.getDate() + deliveryDatesRange[0]);
    date2.setDate(date2.getDate() + deliveryDatesRange[1]);
  }

  const shippingOptions = tile.metadata?.shippingPrices;
  const standardOption = shippingOptions?.find((mode) => mode.standard);
  const standardPrice = standardOption && standardOption.standard.formattedPrice;
  const expressOption = shippingOptions?.find((mode) => mode.express);
  const expressPrice = expressOption && expressOption.express.formattedPrice;

  const ctaText = tile.metadata?.web?.ctaText;

  return (
    <div className="ProductDetails">
      <div className="ProductDetails-navigation">
        {navigationItems.map((item) => (
          <div
            className={classNames("ProductDetails-navigation-item", {
              "ProductDetails-navigation-item--selected": selectedItem === item,
            })}
            key={item}
            onClick={() => setSelectedItem(item)}
          >
            <FormattedMessage id={`cheerz_ProductDetails_${item}`} />
          </div>
        ))}
      </div>
      <div className="ProductDetails-content">
        {selectedItem === "product" && (
          <div className="ProductDetails-product">
            <div className="ProductDetails-product-title">{productTitle}</div>
            {productDescription && (
              <div
                className="ProductDetails-product-description"
                dangerouslySetInnerHTML={{ __html: productDescription }}
              />
            )}
          </div>
        )}
        {selectedItem === "price" && (
          <div className="ProductDetails-price">
            <table>
              <thead>
                <tr>
                  <th className="ProductDetails-price-productName"></th>
                  {priceInfos?.map((product) => {
                    if (product.withQuantity === false) {
                      return (
                        <th className="ProductDetails-price-productName" key={product.productName}>
                          {product.productName}
                        </th>
                      );
                    } else if (product.withQuantity === true && product.products) {
                      return (
                        <>
                          {product.products.map((pack) => (
                            <th className="ProductDetails-price-productName" key={pack.productName}>
                              {pack.productName}
                            </th>
                          ))}
                        </>
                      );
                    }
                  })}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="ProductDetails-price-productTitle">{section.title}</td>
                  {priceInfos?.map((product) => {
                    if (product.withQuantity === false) {
                      return (
                        <td className="ProductDetails-price-productPrice" key={product.productName}>
                          {product.productPrice}
                        </td>
                      );
                    } else if (product.withQuantity === true && product.products) {
                      return (
                        <>
                          {product.products.map((pack) => (
                            <td
                              className="ProductDetails-price-productPrice"
                              key={pack.productName}
                            >
                              {pack.productPrice}
                            </td>
                          ))}
                        </>
                      );
                    }
                  })}
                </tr>
              </tbody>
            </table>
            {(standardPrice || expressPrice) && (
              <div className="ProductDetails-price-delivery">
                <FormattedMessage id="cheerz_ProductDetails_delivery" />
                {" : "}
                {standardPrice && (
                  <FormattedMessage
                    id="cheerz_ProductDetails_delivery_standard"
                    values={{ standardPrice }}
                  />
                )}
                {expressPrice && (
                  <FormattedMessage
                    id="cheerz_ProductDetails_delivery_express"
                    values={{ expressPrice }}
                  />
                )}
              </div>
            )}
          </div>
        )}
        {selectedItem === "delivery" && (
          <div className="ProductDetails-delivery">
            <EstimatedDelivery standardDeliveryDates={[date1.toString(), date2.toString()]} />
          </div>
        )}
      </div>
      <ButtonSmily
        href={getLink(tile.navigationLink, localeSlug)}
        icon={<IconArrowRight />}
        iconPosition="right"
        size="m"
        type="primary"
      >
        {ctaText}
      </ButtonSmily>
    </div>
  );
};
